import { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { dispatchApi, vendorApi, productMappingApi } from '../api/services';
import toast from 'react-hot-toast';
import { Package, Truck, Loader2, Check, X, AlertTriangle } from 'lucide-react';

export default function DispatchReady() {
  const today = new Date().toISOString().slice(0, 10);
  const [vendors, setVendors] = useState([]);
  const [vendorId, setVendorId] = useState('');
  const [shipments, setShipments] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  // Cutoff: only shipments scheduled on or before this date appear in Ready.
  // Defaults to today so future-dated (deferred) shipments don't leak in.
  const [cutoff, setCutoff] = useState(today);
  // Mapping-block confirm dialog: { vendorId, ids, items: [{ key,
  // indian_brand, product_name, suggestion, orders, status }] }.
  const [mappingBlock, setMappingBlock] = useState(null);
  const [confirmingKey, setConfirmingKey] = useState(null);

  useEffect(() => {
    vendorApi.list()
      .then((res) => setVendors((res.data.vendors || res.data || []).filter((v) => v.is_active !== false)))
      .catch((err) => toast.error(err.response?.data?.error || 'Failed to load vendors'));
  }, []);

  async function loadReady() {
    setLoading(true);
    try {
      const params = {};
      if (vendorId) params.vendorId = vendorId;
      if (cutoff) params.on_or_before = cutoff;
      const res = await dispatchApi.listReady(params);
      setShipments(res.data.shipments || []);
      setSelected(new Set());
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load');
    } finally { setLoading(false); }
  }
  useEffect(() => { loadReady(); /* eslint-disable-next-line */ }, [vendorId, cutoff]);

  function toggle(id) {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  }

  const byVendor = useMemo(() => {
    if (!vendorId) {
      const g = {};
      for (const s of shipments) { (g[s.vendor_id] ||= []).push(s); }
      return g;
    }
    return { [vendorId]: shipments };
  }, [shipments, vendorId]);

  async function runDispatch(targetVendorId, ids) {
    setRunning(true);
    try {
      const res = await dispatchApi.run({ vendorId: targetVendorId, shipmentIds: ids });
      const sb = res.data.standardBatch;
      const rb = res.data.reshipBatch;
      let msg = `Dispatched ${res.data.shipmentsDispatched} shipment(s)`;
      if (sb && rb) msg += ` — 2 batches (${sb.batch_number}, ${rb.batch_number})`;
      else if (sb) msg += ` — batch ${sb.batch_number}`;
      else if (rb) msg += ` — reship batch ${rb.batch_number}`;
      toast.success(msg);
      loadReady();
    } catch (err) {
      // Duplicate-customer guard returns a 409 with a conflicts list.
      // Surface the conflicts so Kalpana knows which orders to reschedule.
      if (err.response?.data?.code === 'RESHIP_REASON_MISSING') {
        const list = (err.response.data.missingReason || [])
          .slice(0, 8)
          .map((m) => m.order_id_external)
          .join('\n');
        toast.error(
          'Reship-reason block — set the reship reason on these orders first\n' +
          '(open the order, click the reship badge). It decides the 50%/100% billing:\n' + list,
          { duration: 12000, style: { whiteSpace: 'pre-line' } }
        );
      } else if (err.response?.data?.code === 'UNMAPPED_PRODUCTS') {
        // Confirm-before-mapping dialog: group the unmapped lines by
        // product so the user confirms each product once, not per order.
        const byProduct = new Map();
        for (const u of err.response.data.unmapped || []) {
          const key = `${(u.indian_brand || '').toLowerCase()}|${(u.product_name || '').toLowerCase()}`;
          if (!byProduct.has(key)) {
            byProduct.set(key, {
              key,
              indian_brand: u.indian_brand,
              product_name: u.product_name,
              suggestion: u.suggestion || null,
              orders: [],
              status: 'pending',
            });
          }
          byProduct.get(key).orders.push(u.order_id_external);
        }
        setMappingBlock({ vendorId: targetVendorId, ids, items: [...byProduct.values()] });
      } else if (err.response?.data?.code === 'DUPLICATE_CUSTOMER_SAMEDAY') {
        const list = (err.response.data.conflicts || [])
          .slice(0, 5)
          .map((c) => `${c.conflict_order_external} (${c.customer_name}, ${String(c.scheduled_date).slice(0, 10)})`)
          .join('\n');
        toast.error(
          'Same-customer same-day block. Reschedule one of:\n' + list,
          { duration: 10000, style: { whiteSpace: 'pre-line' } }
        );
      } else {
        toast.error(err.response?.data?.error || 'Dispatch failed');
      }
    } finally { setRunning(false); }
  }

  async function confirmMapping(item) {
    setConfirmingKey(item.key);
    try {
      const res = await productMappingApi.confirmAlias({
        sourceMappingId: item.suggestion.id,
        genericName: item.product_name,
      });
      toast.success(
        `Mapped "${item.product_name}" → ${item.suggestion.indian_brand}` +
        (res.data.rebound ? ` — ${res.data.rebound} order line(s) linked` : '')
      );
      markItem(item.key, 'confirmed');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to confirm mapping');
    } finally { setConfirmingKey(null); }
  }

  function markItem(key, status) {
    setMappingBlock((prev) => {
      if (!prev) return prev;
      const items = prev.items.map((i) => (i.key === key ? { ...i, status } : i));
      // Everything confirmed → close the dialog and re-run the dispatch.
      if (items.every((i) => i.status === 'confirmed')) {
        setTimeout(() => runDispatch(prev.vendorId, prev.ids), 0);
        return null;
      }
      return { ...prev, items };
    });
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-semibold text-gray-900 mb-1">Ready for dispatch</h1>
      <p className="text-sm text-gray-500 mb-4">
        Shipments with a vendor allocated, scheduled to ship today or earlier, parent order approved.
      </p>

      <div className="mb-4 flex items-end gap-3">
        <label className="block">
          <span className="block text-xs uppercase text-gray-500 mb-1">Vendor</span>
          <select value={vendorId} onChange={(e) => setVendorId(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
            <option value="">All vendors (grouped)</option>
            {vendors.map((v) => (<option key={v.id} value={v.id}>{v.name} ({v.code})</option>))}
          </select>
        </label>
        <label className="block">
          <span className="block text-xs uppercase text-gray-500 mb-1">Scheduled on or before</span>
          <input type="date" value={cutoff} onChange={(e) => setCutoff(e.target.value || today)}
                 className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
        </label>
        <button onClick={() => setCutoff(today)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
                title="Reset cutoff to today (hide deferred shipments)">
          Today
        </button>
        <button onClick={loadReady} className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">
          Refresh
        </button>
      </div>

      {loading && <div className="text-sm text-gray-500"><Loader2 className="w-4 h-4 animate-spin inline mr-1" />Loading…</div>}

      {!loading && shipments.length === 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <Package className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600">No shipments ready to dispatch right now.</p>
          <p className="text-xs text-gray-500 mt-2">
            Allocate shipments to a vendor from the order detail page first, then approve the order.
          </p>
        </div>
      )}

      {Object.entries(byVendor).map(([vid, list]) => {
        const vendor = vendors.find((v) => v.id === vid);
        const vendorName = vendor ? `${vendor.name} (${vendor.code})` : vid.slice(0, 8);
        const picked = list.filter((s) => selected.has(s.id));
        // Group shipments within this vendor by order — one visual row per
        // order, with shipment count + total qty, instead of one row per
        // shipment (was visually confusing: same order id appeared N times
        // for N-item orders).
        const byOrder = new Map();
        for (const s of list) {
          if (!byOrder.has(s.order_id)) byOrder.set(s.order_id, []);
          byOrder.get(s.order_id).push(s);
        }
        const orderGroups = Array.from(byOrder.entries());
        return (
          <div key={vid} className="bg-white rounded-xl border border-gray-200 mb-4 overflow-hidden">
            <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
              <div>
                <div className="font-medium">{vendorName}</div>
                <div className="text-xs text-gray-500">
                  {orderGroups.length} order(s) · {list.length} shipment(s)
                </div>
              </div>
              <button
                disabled={picked.length === 0 || running}
                onClick={() => runDispatch(vid, picked.map((s) => s.id))}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Truck className="w-4 h-4" />}
                Dispatch {picked.length || list.length} to vendor
              </button>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs font-semibold uppercase text-gray-500">
                <tr>
                  <th className="w-8 px-3 py-2">
                    <input type="checkbox"
                           checked={list.every((s) => selected.has(s.id))}
                           onChange={() => {
                             const next = new Set(selected);
                             if (list.every((s) => next.has(s.id))) list.forEach((s) => next.delete(s.id));
                             else list.forEach((s) => next.add(s.id));
                             setSelected(next);
                           }} />
                  </th>
                  <th className="text-left px-4 py-2">Order</th>
                  <th className="text-left px-4 py-2">Customer</th>
                  <th className="text-right px-4 py-2">Items</th>
                  <th className="text-right px-4 py-2">Total qty</th>
                  <th className="text-left px-4 py-2">Scheduled</th>
                  <th className="text-left px-4 py-2">Type</th>
                </tr>
              </thead>
              <tbody>
                {orderGroups.map(([orderId, shipmentsInOrder]) => {
                  const first = shipmentsInOrder[0];
                  const totalQty = shipmentsInOrder.reduce((a, s) => a + (s.quantity || 0), 0);
                  const allPicked = shipmentsInOrder.every((s) => selected.has(s.id));
                  const anyPicked = shipmentsInOrder.some((s) => selected.has(s.id));
                  const dates = Array.from(new Set(shipmentsInOrder.map((s) => String(s.scheduled_date).slice(0, 10))));
                  return (
                    <tr key={orderId} className="border-t border-gray-100 hover:bg-gray-50">
                      <td className="px-3 py-2">
                        <input type="checkbox"
                               ref={(el) => { if (el) el.indeterminate = anyPicked && !allPicked; }}
                               checked={allPicked}
                               onChange={() => {
                                 const next = new Set(selected);
                                 if (allPicked) shipmentsInOrder.forEach((s) => next.delete(s.id));
                                 else shipmentsInOrder.forEach((s) => next.add(s.id));
                                 setSelected(next);
                               }} />
                      </td>
                      <td className="px-4 py-2 font-mono text-blue-600">
                        <Link to={`/orders/${orderId}`}>{first.order_id_external}</Link>
                      </td>
                      <td className="px-4 py-2">{first.customer_name}</td>
                      <td className="px-4 py-2 text-right">
                        {shipmentsInOrder.length}
                      </td>
                      <td className="px-4 py-2 text-right">{totalQty}</td>
                      <td className="px-4 py-2">{dates.join(', ')}</td>
                      <td className="px-4 py-2">
                        {first.is_reship
                          ? <span className="text-purple-700 font-medium">reship</span>
                          : <span className="text-gray-500">standard</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })}

      {mappingBlock && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full p-6 max-h-[85vh] overflow-y-auto">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Confirm product mappings</h2>
            <p className="text-sm text-gray-500 mb-4">
              These products are not in the product master yet. Nothing is dispatched until
              each one is confirmed against an existing entry — or mapped manually on the
              Product Mapping page. Every confirmation is recorded in the audit log.
            </p>

            <div className="space-y-3">
              {mappingBlock.items.map((item) => (
                <div key={item.key} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="font-medium text-gray-900">{item.product_name}</div>
                      <div className="text-xs text-gray-500 mt-0.5">
                        Brand: {item.indian_brand || '—'} · Order(s): {item.orders.join(', ')}
                      </div>
                    </div>
                    {item.status === 'confirmed' && (
                      <span className="flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 px-2 py-1 rounded-full whitespace-nowrap">
                        <Check className="w-3 h-3" /> Confirmed
                      </span>
                    )}
                  </div>

                  {item.status === 'pending' && item.suggestion && (
                    <div className="mt-3 bg-blue-50 border border-blue-100 rounded-lg p-3">
                      <div className="text-xs uppercase text-blue-700 font-semibold mb-1">Closest master entry</div>
                      <div className="text-sm text-gray-800">
                        {item.suggestion.generic_name}
                        {item.suggestion.container ? <span className="text-gray-500"> · {item.suggestion.container}</span> : null}
                      </div>
                      <div className="mt-2 flex gap-2">
                        <button
                          disabled={confirmingKey === item.key}
                          onClick={() => confirmMapping(item)}
                          className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 disabled:opacity-50"
                        >
                          {confirmingKey === item.key
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : <Check className="w-3 h-3" />}
                          Same product — confirm
                        </button>
                        <button
                          disabled={confirmingKey === item.key}
                          onClick={() => markItem(item.key, 'rejected')}
                          className="flex items-center gap-1 px-3 py-1.5 border border-gray-300 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-50 disabled:opacity-50"
                        >
                          <X className="w-3 h-3" /> Not the same
                        </button>
                      </div>
                    </div>
                  )}

                  {item.status !== 'confirmed' && (!item.suggestion || item.status === 'rejected') && (
                    <div className="mt-3 flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg p-3">
                      <AlertTriangle className="w-4 h-4 shrink-0" />
                      <span>
                        {item.suggestion ? 'Needs manual mapping — ' : 'No close match found — '}
                        add it on the{' '}
                        <Link to="/admin/product-mapping" className="underline font-medium">Product Mapping page</Link>,
                        then re-run the dispatch.
                      </span>
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => setMappingBlock(null)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
              >
                Close
              </button>
              <button
                disabled={running || confirmingKey !== null}
                onClick={() => {
                  const mb = mappingBlock;
                  setMappingBlock(null);
                  runDispatch(mb.vendorId, mb.ids);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                <Truck className="w-4 h-4" /> Re-run dispatch
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
